import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * A dense data table.
 *
 * The header is sticky because these lists get long and a scrolled table with no visible
 * column names is unreadable. Rows are 40px — tight, but with a 32px control fitting inside a
 * cell without the row growing.
 */
export function Table({ className, ...props }: React.HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto scrollbar-subtle">
      <table className={cn("w-full caption-bottom border-collapse text-sm", className)} {...props} />
    </div>
  );
}

export function TableHeader({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn("sticky top-0 z-10 bg-surface [&_tr]:border-b [&_tr]:border-border", className)}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}

export function TableRow({
  className, interactive, ...props
}: React.HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean }) {
  return (
    <tr
      className={cn(
        "border-b border-border transition-colors",
        interactive && "cursor-pointer hover:bg-surface-hover focus-visible:bg-surface-hover",
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        "h-9 whitespace-nowrap px-3 text-left align-middle text-2xs font-medium uppercase",
        "tracking-wide text-text-subtle",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn("h-10 px-3 align-middle text-text", className)} {...props} />;
}

/** A cell whose content people compare character by character — extensions, realms, ids. */
export function MonoCell({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <TableCell className={cn("font-mono text-xs", className)} {...props} />;
}
